import {
    AfterViewChecked,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { RecordField } from '@taxilla-library/lib/models/record/recordfield.class';
import { AlertError } from '@taxilla-library/lib/store/actions/shared.actions';
import { Store } from '@ngrx/store';
import { TranslationService } from '@translation';
import { CommonUtilsService } from '@taxilla-library/lib/services/commonutils/common-utils.service';

import { ModifiedErrorStateMatcher } from '../../extensions/errorstatematcher/errorstatematcher';

@Component({
    selector: 'material-input-date',
    templateUrl: './inputdate.component.html',
    styleUrls: ['./inputdate.component.scss'],
    standalone: false,
})
export class MaterialInputDateComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
    @Input() placeholder: any = '';
    @Input() name: any = '';
    @Input() title: any = '';
    @Input() model;
    @Input() id;
    @Input() classList;
    @Input() debounceAfter: number;
    @Input() skipStartDate;
    @Input() required: boolean;
    @Input() errors: any[] = [];
    @Input() warnings: any[] = [];
    @Input() disabled: boolean;
    @Input() description: string;
    @Input() dateFormat = 'dd/mm/yyyy';
    @Input() inputOnlyDisabled: boolean;
    @Input() appearance: MatFormFieldAppearance;
    @Input() formFieldClass: string;
    @Input() isSearchField: boolean;
    @Input() label: string;
    @Input() minDate: string;
    @Input() minDateFormat: string;
    @Input() maxDate: string;
    @Input() maxDateFormat: string;
    @Input() showError: boolean;
    @Input() field: RecordField;
    @Input() dontEmitFirstEvent: boolean;

    @Output() modelChange = new EventEmitter();
    @Output() debounceEventHandler = new EventEmitter();
    @Output() formatChange = new EventEmitter<'date' | 'text'>();

    @ViewChildren('dateInputElement') dateInputElements: QueryList<ElementRef<HTMLInputElement>>;

    matcher = new ModifiedErrorStateMatcher();
    minDateValue: Date;
    maxDateValue: Date;
    mannualInput: any;
    originalInput: any;
    isMasked = false;

    constructor(private _commonUtils: CommonUtilsService, private store$: Store, private _translate: TranslationService) {}

    private isMaskedValue = (value: any): boolean => typeof value === 'string' && value.includes('*');

    ngOnChanges(changes: SimpleChanges) {
        if (changes?.model && changes?.model?.currentValue !== changes?.model?.previousValue) {
            this.applyChangesToDate(changes.model.currentValue, changes?.model?.firstChange && this.dontEmitFirstEvent);
        }
        if (changes?.minDate || changes?.maxDate) {
            this.applyDateRestriction();
        }
    }

    ngAfterViewChecked(): void {
        if (this.isMasked && this.mannualInput && this.dateInputElements?.first?.nativeElement) {
            const inputEl = this.dateInputElements.first.nativeElement;
            if (inputEl.value !== this.mannualInput) {
                inputEl.value = this.mannualInput;
            }
        }
    }

    onInput = (event: any) => {
        this.originalInput = event.target.value;

        if (this.isMasked && !this.originalInput?.includes('*')) {
            this.isMasked = false;
        }

        // Check if input contains curly braces - if so, switch to text mode
        if (this.originalInput && (this.originalInput.includes('{') || this.originalInput.includes('}'))) {
            this.formatChange.emit('text');
        } else if (!this.originalInput || this.originalInput.trim() === '') {
            // If input is cleared, switch back to date mode
            this.formatChange.emit('date');
        }
    };

    onFocusOut = () => {
        if (this.isMasked && this.originalInput && this.originalInput.includes('*')) {
            return;
        }
        if (this.originalInput && this.dateFormat) {
            const result: { isValid: boolean; formattedDate?: string; error?: string } =
                CommonUtilsService.formatAnyDateStringToSpecificFormat(this.originalInput, this.dateFormat);
            if (result.isValid) {
                this.clearErrors();
                this.applyChangesToDate(result.formattedDate);
            } else {
                this.errors = [];
                this.mannualInput = undefined;
                this.originalInput = undefined;
                this.errors.push('Please enter a valid date in valid format.');
            }
        } else if (this.originalInput === '') {
            // User explicitly cleared the input text — propagate the cleared state
            this.applyChangesToDate(undefined);
        }
    };

    onKeyEnter = () => {
        if (this.isMasked && this.originalInput && this.originalInput.includes('*')) {
            return;
        }
        if (this.originalInput && this.dateFormat) {
            const result: { isValid: boolean; formattedDate?: string; error?: string } =
                CommonUtilsService.formatAnyDateStringToSpecificFormat(this.originalInput, this.dateFormat);
            if (result.isValid) {
                this.clearErrors();
                this.applyChangesToDate(result.formattedDate);
            } else {
                this.errors = [];
                this.store$.dispatch(AlertError({ message: this.translateMsg('Enter a valid value to proceed') }));
                this.field.message.errors.push('Please enter a valid date');
            }
        }
    };

    translateMsg = (msg: string): string => this._translate.translate('' + msg);

    applyChangesToDate = (date: string, dontEmitEvent?: boolean) => {
        if (date == null) {
            this.originalInput = undefined;
            this.mannualInput = undefined;
            this.isMasked = false;
            !dontEmitEvent && this.emitChanges(undefined);
            return;
        }
        if (this.isMaskedValue(date)) {
            this.isMasked = true;
            this.originalInput = date;
            this.mannualInput = date;
            !dontEmitEvent && this.emitChanges(date);
            return;
        }
        this.isMasked = false;
        let dateString: string;
        if ((date as any) instanceof Date) {
            dateString = date;
        } else {
            dateString = this._commonUtils.transformDateToLocale(date, this.dateFormat as any, 'DATE', true);
        }
        this.mannualInput = dateString;
        !dontEmitEvent && this.emitChanges(dateString);
    };

    private clearErrors = () => {
        this.errors = [];
        if (this.field?.message?.errors?.length > 0) {
            this.field.message.errors.splice(0, this.field.message.errors.length);
        }
    };

    private applyDateRestriction = () => {
        if (this.minDate && this.minDateFormat) {
            this.minDateValue = CommonUtilsService.transformDate(this.minDate, this.minDateFormat as any, 'DATE') as any;
        } else {
            this.minDateValue = undefined;
        }
        if (this.maxDate && this.maxDateFormat) {
            this.maxDateValue = CommonUtilsService.transformDate(this.maxDate, this.maxDateFormat as any, 'DATE') as any;
        } else {
            this.maxDateValue = undefined;
        }
    };

    private emitChanges = dateTime => {
        if (dateTime && dateTime.getDate) {
            const transformed = this._commonUtils.transformDateToLocale(dateTime, 'DATE', this.dateFormat as any, true);
            // Only update model if the transform produced a valid string; otherwise preserve existing value
            if (transformed != null) {
                this.model = transformed;
            }
        } else {
            this.model = dateTime;
        }
        this.modelChange.emit(this.model);
    };

    onDatePickerClose = () => {
        if (this.mannualInput) {
            this.isMasked = false;
            this.clearErrors();
            this.emitChanges(this.mannualInput);
        }
    };

    ngOnInit() {
        if (this?.minDate || this?.maxDate) {
            this.applyDateRestriction();
        }
    }

    ngOnDestroy(): void {}
}
